import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useCASL, ACTIONS, SUBJECTS } from "../../contexts/CASLContext";
import { useEffect, useState } from "react";

// ══════════════════════════════════════════════════════════
// CASL PROTECTED ROUTE
// ══════════════════════════════════════════════════════════

export function CASLProtectedRoute({
  children,
  action,
  subject,
  field,
  fallback = null,
  redirectTo = "/unauthorized",
}) {
  const { user, loading, can } = useCASL();

  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check CASL permission
  if (action && subject && !can(action, subject, field)) {
    return fallback || <Navigate to={redirectTo} replace />;
  }

  return children;
}

// ══════════════════════════════════════════════════════════
// CASL CONDITIONAL RENDERING
// ══════════════════════════════════════════════════════════

/**
 * Render children only if user has CASL permission
 */
export function CASLCan({
  action,
  subject,
  field,
  actions = [], // Multiple actions (OR logic)
  children,
  fallback = null,
}) {
  const { can, canAny } = useCASL();

  // Check multiple actions
  if (actions.length > 0 && subject) {
    if (!canAny(actions, subject, field)) {
      return fallback;
    }
    return children;
  }

  // Check single action
  if (action && subject) {
    if (!can(action, subject, field)) {
      return fallback;
    }
  }

  return children;
}

/**
 * Render children only if user CANNOT perform action
 */
export function CASLCannot({
  action,
  subject,
  field,
  children,
  fallback = null,
}) {
  const { cannot } = useCASL();

  if (!cannot(action, subject, field)) {
    return fallback;
  }

  return children;
}

/**
 * Render different content based on what user can do
 */
export function CASLSwitch({ subject, field, whenCan = {}, fallback = null }) {
  const { can } = useCASL();

  // Check each action in order
  for (const [action, component] of Object.entries(whenCan)) {
    if (can(action, subject, field)) {
      return component;
    }
  }

  return fallback;
}

// ══════════════════════════════════════════════════════════
// PERMISSION-AWARE BUTTON
// ══════════════════════════════════════════════════════════

export function CASLButton({
  action,
  subject,
  field,
  children,
  onClick,
  ...props
}) {
  const { can } = useCASL();

  const hasPermission = can(action, subject, field);

  return (
    <Button
      {...props}
      disabled={!hasPermission || props.disabled}
      onClick={hasPermission ? onClick : undefined}
    >
      {children}
    </Button>
  );
}

// ══════════════════════════════════════════════════════════
// INSTANCE-BASED PERMISSIONS
// ══════════════════════════════════════════════════════════

/**
 * Check permissions on a specific resource instance
 * Useful for checking conditions like "can update own posts"
 */
export function CASLCanInstance({
  action,
  instance, // The actual object with conditions
  field,
  children,
  fallback = null,
}) {
  const { ability } = useCASL();

  if (!ability || !instance) {
    return fallback;
  }

  // CASL checks conditions on the instance
  if (!ability.can(action, instance, field)) {
    return fallback;
  }

  return children;
}

/**
 * Filter array of items based on CASL rules
 */
export function useAccessibleItems(items, action = ACTIONS.READ) {
  const { ability } = useCASL();

  if (!ability || !items) {
    return [];
  }

  // Filter items based on CASL rules
  return items.filter((item) => ability.can(action, item));
}

/**
 * Check if user can access specific instance
 */
export function useCanAccessInstance(action, instance, field) {
  const { ability } = useCASL();

  if (!ability || !instance) {
    return false;
  }

  return ability.can(action, instance, field);
}

// ══════════════════════════════════════════════════════════
// FIELD-LEVEL PERMISSIONS
// ══════════════════════════════════════════════════════════

/**
 * Render field only if user can access it
 */
export function CASLField({
  action = ACTIONS.READ,
  subject,
  field,
  instance,
  children,
  fallback = null,
}) {
  const { ability } = useCASL();

  if (!ability) {
    return fallback;
  }

  // Check field-level permission
  const target = instance || subject;
  if (!ability.can(action, target, field)) {
    return fallback;
  }

  return children;
}

/**
 * Hook to check if fields are accessible
 */
export function useFieldAccess(action, subject, fields = []) {
  const { ability } = useCASL();

  if (!ability || fields.length === 0) {
    return {};
  }

  const access = {};
  fields.forEach((field) => {
    access[field] = ability.can(action, subject, field);
  });

  return access;
}

// ══════════════════════════════════════════════════════════
// WORK UNIT SCOPED COMPONENTS
// ══════════════════════════════════════════════════════════

/**
 * Show content only if user has access to work unit
 */
export function CASLWorkUnitAccess({ workUnitId, children, fallback = null }) {
  const { hasWorkUnitAccess } = useCASL();

  if (!hasWorkUnitAccess(workUnitId)) {
    return fallback;
  }

  return children;
}

/**
 * Filter items by work unit access
 */
export function useWorkUnitFilteredItems(items) {
  const { getUserWorkUnits } = useCASL();
  const userWorkUnits = getUserWorkUnits();

  if (!items) return [];

  return items.filter((item) => {
    const itemWorkUnit = item.workUnit?._id || item.workUnit;
    return userWorkUnits.includes(itemWorkUnit);
  });
}

// ══════════════════════════════════════════════════════════
// QUERY FILTER HOOK
// ══════════════════════════════════════════════════════════

/**
 * Hook to build API query filters based on user abilities
 */
export function useCASLQueryFilter(subject) {
  const { buildQueryFilter } = useCASL();
  return buildQueryFilter(subject);
}

/**
 * Hook to fetch data with CASL filtering
 */
export function useCASLFetch(url, subject, options = {}) {
  const { token, buildQueryFilter } = useCASL();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const filter = buildQueryFilter(subject);
      const queryParams = new URLSearchParams({
        ...filter,
        ...options.params,
      }).toString();

      const response = await fetch(`${url}?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [url, subject]);

  return { data, loading, error, refetch: fetchData };
}

// ══════════════════════════════════════════════════════════
// TABLE ACTIONS GENERATOR
// ══════════════════════════════════════════════════════════

/**
 * Generate table actions based on CASL permissions
 */
export function useTableActions(subject, instance = null) {
  const { can } = useCASL();

  const target = instance || subject;

  return {
    canView: can(ACTIONS.READ, target),
    canEdit: can(ACTIONS.UPDATE, target),
    canDelete: can(ACTIONS.DELETE, target),
    canCreate: can(ACTIONS.CREATE, subject),
    canManage: can(ACTIONS.MANAGE, target),
    canApprove: can(ACTIONS.APPROVE, target),
    canReject: can(ACTIONS.REJECT, target),
    canExport: can(ACTIONS.EXPORT, target),
    canImport: can(ACTIONS.IMPORT, target),
  };
}

// ══════════════════════════════════════════════════════════
// UNAUTHORIZED PAGE
// ══════════════════════════════════════════════════════════

export function CASLUnauthorizedPage() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        gap: 3,
        px: 3,
      }}
    >
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: "rgba(213, 0, 0, 0.05)",
          border: "1px solid rgba(213, 0, 0, 0.2)",
        }}
      >
        <LockIcon sx={{ fontSize: 64, color: "error.main" }} />
      </Box>

      <Box sx={{ textAlign: "center", maxWidth: 500 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          You don't have permission to access this resource. Please contact your
          administrator if you believe this is an error.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
        <Button variant="contained" onClick={() => navigate("/")}>
          Go Home
        </Button>
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════
// EXPORT ALL
// ══════════════════════════════════════════════════════════

export { useCASL, ACTIONS, SUBJECTS };
